"use client";

import { c as _c } from "react/compiler-runtime";
import { cn } from "@/lib/utils";
import { useFillViewportHeight } from "@/hooks/useFillViewportHeight";
import { ScrollArea as MantineScrollArea, type ScrollAreaProps as MantineScrollAreaProps, type ScrollAreaStylesNames } from "@mantine/core";
import styles from "./scroll-area.module.scss";
const scrollAreaClassNames = {
  root: styles.root,
  viewport: styles.viewport,
  scrollbar: styles.scrollbar,
  thumb: styles.thumb,
  corner: styles.corner,
  content: styles.content
} as const;

/** Mantine Select dropdown scroll — same styles as `<ScrollArea>`. */
export const mantineScrollAreaProps = {
  type: "hover" as const,
  scrollbars: "y" as const,
  scrollbarSize: 6,
  classNames: scrollAreaClassNames
} as const;
export type ScrollAreaProps = Readonly<MantineScrollAreaProps & {
  className?: string;
  classNames?: Partial<Record<ScrollAreaStylesNames, string>>;
  /** Size height from the element top to the viewport bottom (updates on resize). */
  fillViewport?: boolean;
  safeAreaBottom?: boolean;
}>;

/** Thin wrapper around Mantine `ScrollArea` with design-system scrollbar styles. */
export function ScrollArea(t0) {
  const $ = _c(46);
  let children;
  let className;
  let classNames;
  let props;
  let style;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      children,
      className,
      classNames,
      fillViewport: t1,
      safeAreaBottom: t2,
      style,
      type: t3,
      scrollbars: t4,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = classNames;
    $[4] = props;
    $[5] = style;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    children = $[1];
    className = $[2];
    classNames = $[3];
    props = $[4];
    style = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
  }
  const fillViewport = t1 === undefined ? false : t1;
  const safeAreaBottom = t2 === undefined ? false : t2;
  const type = t3 === undefined ? "hover" : t3;
  const scrollbars = t4 === undefined ? "y" : t4;
  let t5;
  if ($[10] !== fillViewport) {
    t5 = {
      enabled: fillViewport
    };
    $[10] = fillViewport;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    ref,
    style: fillStyle
  } = useFillViewportHeight(t5);
  const t6 = fillViewport ? ref : undefined;
  let t7;
  if ($[12] !== fillStyle || $[13] !== style) {
    t7 = {
      ...fillStyle,
      ...style
    };
    $[12] = fillStyle;
    $[13] = style;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = fillViewport && styles.fillViewport;
  const t9 = classNames?.root;
  let t10;
  if ($[15] !== className || $[16] !== t8 || $[17] !== t9) {
    t10 = cn(scrollAreaClassNames.root, t8, className, t9);
    $[15] = className;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = classNames?.viewport;
  let t12;
  if ($[19] !== t11) {
    t12 = cn(scrollAreaClassNames.viewport, t11);
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const t13 = classNames?.scrollbar;
  let t14;
  if ($[21] !== t13) {
    t14 = cn(scrollAreaClassNames.scrollbar, t13);
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const t15 = classNames?.thumb;
  let t16;
  if ($[23] !== t15) {
    t16 = cn(scrollAreaClassNames.thumb, t15);
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  const t17 = classNames?.corner;
  let t18;
  if ($[25] !== t17) {
    t18 = cn(scrollAreaClassNames.corner, t17);
    $[25] = t17;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  const t19 = fillViewport && styles.fillViewportContent;
  const t20 = safeAreaBottom && styles.safeAreaBottomContent;
  const t21 = classNames?.content;
  let t22;
  if ($[27] !== t19 || $[28] !== t20 || $[29] !== t21) {
    t22 = cn(scrollAreaClassNames.content, t19, t20, t21);
    $[27] = t19;
    $[28] = t20;
    $[29] = t21;
    $[30] = t22;
  } else {
    t22 = $[30];
  }
  let t23;
  if ($[31] !== t10 || $[32] !== t12 || $[33] !== t14 || $[34] !== t16 || $[35] !== t18 || $[36] !== t22) {
    t23 = {
      root: t10,
      viewport: t12,
      scrollbar: t14,
      thumb: t16,
      corner: t18,
      content: t22
    };
    $[31] = t10;
    $[32] = t12;
    $[33] = t14;
    $[34] = t16;
    $[35] = t18;
    $[36] = t22;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] !== children || $[39] !== props || $[40] !== scrollbars || $[41] !== t23 || $[42] !== t6 || $[43] !== t7 || $[44] !== type) {
    t24 = <MantineScrollArea ref={t6} type={type} scrollbars={scrollbars} style={t7} classNames={t23} {...props}>{children}</MantineScrollArea>;
    $[38] = children;
    $[39] = props;
    $[40] = scrollbars;
    $[41] = t23;
    $[42] = t6;
    $[43] = t7;
    $[44] = type;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  return t24;
}